import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { jourCivilAujourdhui } from '../utils/dates';
import {
  Dialog,
  DialogTitle,
  Divider,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Send as SendIcon,
  QrCodeScanner as ScanIcon,
  WarningAmber as AlerteIcon,
} from '@mui/icons-material';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import { quantiteACombler, estEnAlerte } from '../utils/reappro';
import { SEPARATEUR_NUMERO, ELISION } from '../utils/numeroCommande';
import { PILE_CHASSE_FIXE } from '../theme/jetons';
import { useStock } from '../contexts/StockContext';
import ZXingBarcodeScanner from './ZXingBarcodeScanner';

const PurchaseOrderFormWithRemises = ({
  open,
  onClose,
  onSubmit,
  editingOrder = null,
  panierInitial = null,
  suppliers = [],
  products = [],
  showSnackbar
}) => {
  const { t } = useLanguage();
  const { currentStock } = useStock();
  const [formData, setFormData] = useState({
    supplier_id: '',
    order_date: jourCivilAujourdhui(),
    // Facultative : le serveur la sert déjà (`expected_delivery_date`), le
    // formulaire ne la demandait pas. Elle est la seule date que le panneau de
    // détail peut montrer, faute de date de réception (cf. décision 12).
    expected_delivery_date: '',
    comment: '',
    items: [],
    vat_enabled: false,
    vat_rate: ''
  });

  // ⚠️ IL N'EN RESTE QU'UN. Les cinq autres champs de saisie (`quantity`,
  // `unitPrice`, `remiseType`, `remiseValue`, `itemComment`) servaient le
  // formulaire d'ajout ligne par ligne, remplacé par `ajouterProduit` : le
  // produit désigné entre AVEC ses valeurs du catalogue, et tout se corrige
  // ensuite dans le tableau. *Ce champ-ci survit parce qu'il porte encore la
  // désignation* — c'est le sélecteur de produit, pas une saisie.
  const [selectedProduct, setSelectedProduct] = useState('');
  const [existingItems, setExistingItems] = useState([]);
  const [scanOuvert, setScanOuvert] = useState(false);

  // Ne proposer que les produits du fournisseur sélectionné
  const filteredProducts = useMemo(() => {
    if (!formData.supplier_id) {
      return [];
    }
    return products.filter((p) => String(p.supplier_id) === String(formData.supplier_id));
  }, [products, formData.supplier_id]);

  // Devise = celle du fournisseur sélectionné (mono-fournisseur), fallback EUR
  const selectedSupplier = suppliers.find((s) => String(s.id) === String(formData.supplier_id));
  const currency = selectedSupplier?.currency || 'EUR';
  const currencySymbol = getCurrencySymbol(currency);

  // ── LE NUMÉRO PENDANT LA SAISIE ─────────────────────────────────────────
  // 🔴 LA SÉQUENCE EST ÉLIDÉE, ET C'EST MESURÉ. Elle vaut `max(suffixes du
  // stock) + 1` calculé AU MOMENT DU POST, suivie d'une boucle anti-collision
  // qui la fait monter jusqu'à dix fois — cette boucle existe précisément parce
  // que deux clients du même stock peuvent enregistrer en même temps. Un numéro
  // prévisualisé peut donc changer, et une route de prévisualisation ne
  // supprimerait pas l'incertitude : elle la déplacerait d'une seconde.
  // ⚠️ LES DEUX AUTRES SEGMENTS, EUX, SONT EXACTS — le stock et le fournisseur
  // qu'on vient de choisir. On montre ce qui est vrai et on élide le reste :
  // exact dans ce qu'il montre, honnête sur ce qu'il ne peut pas savoir.
  // En ÉDITION le numéro existe : il est rendu entier.
  const numeroAffiche = editingOrder
    ? editingOrder.order_number
    : [currentStock?.name, selectedSupplier?.company_name, ELISION]
        .filter(Boolean).join(SEPARATEUR_NUMERO);

  // ── LES PRODUITS SOUS LE SEUIL, CHEZ CE FOURNISSEUR ─────────────────────
  // `filteredProducts` porte déjà le filtre fournisseur ; `estEnAlerte` reprend
  // la comparaison INCLUSIVE du serveur (`current_stock <= min_stock`), et
  // `quantiteACombler` la quantité qui en sort — la même fonction que le
  // dialogue d'ajustement et le sélecteur de réappro.
  const produitsEnAlerte = useMemo(
    () => filteredProducts.filter(estEnAlerte),
    [filteredProducts]);

  const dejaDansLaCommande = (produitId) =>
    formData.items.some((i) => String(i.product_id) === String(produitId))
    || existingItems.some((i) => String(i.product_id) === String(produitId));

  // Ajouter UNE ligne depuis le champ unique. La quantité de départ comble le
  // seuil si le produit est en alerte, sinon 1 — et le prix vient du catalogue.
  // Les deux se corrigent dans le tableau : *une valeur de départ n'est pas une
  // décision, c'est une économie de frappe.*
  const ajouterProduit = (produitId) => {
    const produit = products.find((p) => String(p.id) === String(produitId));
    if (!produit) return;
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, {
        product_id: produit.id,
        product_name: produit.name,
        product_barcode: produit.barcode,
        current_stock: produit.current_stock,
        min_stock: produit.min_stock,
        quantity: quantiteACombler(produit) || 1,
        unit_price: parseFloat(produit.price) || 0,
        remise_type: 'montant',
        remise_value: 0,
        comment: '',
      }],
    }));
    setSelectedProduct('');
  };

  // ⚠️ LE SCAN AJOUTE, IL NE REMPLIT PAS UN CHAMP. Un code scanné qui atterrit
  // dans une zone de saisie demande un second geste ; ici il désigne un produit,
  // donc il ajoute la ligne. Un code inconnu du fournisseur choisi le DIT —
  // silence sur un scan est indistinguable d'un scan raté.
  const surCodeScanne = (code) => {
    setScanOuvert(false);
    const produit = filteredProducts.find((p) => String(p.barcode) === String(code).trim());
    if (produit) ajouterProduit(produit.id);
    else if (showSnackbar) showSnackbar(t('purchaseOrderForm.barcodeNotFound'), 'warning');
  };

  const ajouterProduitsEnAlerte = () => {
    // ⚠️ ON N'AJOUTE PAS CE QUI Y EST DÉJÀ. Sans ce filtre, un second clic
    // doublerait les lignes — et deux lignes du même produit sont LÉGITIMES
    // par ailleurs (mesuré en base), donc rien ne les signalerait comme un
    // doublon accidentel.
    const aAjouter = produitsEnAlerte.filter((p) => !dejaDansLaCommande(p.id));
    if (aAjouter.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, ...aAjouter.map((p) => ({
        product_id: p.id,
        product_name: p.name,
        product_barcode: p.barcode,
        current_stock: p.current_stock,
        min_stock: p.min_stock,
        quantity: quantiteACombler(p),
        unit_price: parseFloat(p.price) || 0,
        remise_type: 'montant',
        remise_value: 0,
        comment: '',
      }))],
    }));
  };

  // 🔴 LE PANIER REPRIS PRÉ-REMPLIT LA CRÉATION, jamais l'édition — modifier une
  // commande existante avec le contenu d'un panier écraserait ce qu'elle porte.
  // Les lignes arrivent DÉJÀ REVALIDÉES par le bandeau : ce formulaire ne
  // revalide pas une seconde fois, il ferait double emploi et pourrait diverger.
  useEffect(() => {
    if (!panierInitial || editingOrder) return;
    setFormData((prev) => ({
      ...prev,
      supplier_id: panierInitial.supplier_id,
      items: panierInitial.lignes.map((l) => ({
        product_id: l.product_id,
        product_name: l.product_name,
        quantity: l.quantity,
        unit_price: l.unit_price,
        remise_type: 'montant',
        remise_value: 0,
        comment: '',
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panierInitial, editingOrder]);

  useEffect(() => {
    if (editingOrder) {
      setFormData({
        supplier_id: editingOrder.supplier_id || '',
        // Le serveur sert `order_date` NUE depuis le lot T2 ; le `split('T')`
        // reste pour une réponse qui porterait encore une heure, et il ne
        // convertit rien — c'est bien un jour civil qu'on repose tel quel.
        order_date: editingOrder.order_date
          ? editingOrder.order_date.split('T')[0] : jourCivilAujourdhui(),
        expected_delivery_date: editingOrder.expected_delivery_date
          ? editingOrder.expected_delivery_date.split('T')[0] : '',
        comment: editingOrder.comment || '',
        items: [],
        vat_enabled: !!editingOrder.vat_enabled,
        vat_rate: editingOrder.vat_rate != null ? String(editingOrder.vat_rate) : ''
      });
      
      // Initialiser les produits existants
      setExistingItems(editingOrder.items || []);
      
      // Réinitialiser les champs de saisie pour éviter la confusion
      setSelectedProduct('');
    } else {
      setFormData({
        supplier_id: '',
        order_date: jourCivilAujourdhui(),
        comment: '',
        items: [],
        vat_enabled: false,
        vat_rate: ''
      });

      // Réinitialiser les produits existants
      setExistingItems([]);
      
      // Réinitialiser les champs de saisie
      setSelectedProduct('');
    }
  }, [editingOrder]);

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateNewItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleUpdateExistingItem = (index, field, value) => {
    setExistingItems(prev => 
      prev.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const handleRemoveExistingItem = (index) => {
    setExistingItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateItemTotal = (item) => {
    const total = item.quantity * item.unit_price;
    let remiseAmount = 0;
    
    if (item.remise_type === 'pourcentage') {
      remiseAmount = total * (item.remise_value / 100);
    } else {
      remiseAmount = item.remise_value;
    }
    
    return total - remiseAmount;
  };

  const calculateOrderTotal = () => {
    // Calculer le total des produits existants + nouveaux produits
    const existingItemsTotal = existingItems.reduce((total, item) => total + calculateItemTotal(item), 0);
    const newItemsTotal = formData.items.reduce((total, item) => total + calculateItemTotal(item), 0);
    return existingItemsTotal + newItemsTotal;
  };

  // TVA (option a : total_final reste HT, TVA appliquée après remises)
  const calculateVatAmount = () => {
    if (!formData.vat_enabled || formData.vat_rate === '') return 0;
    const ht = calculateOrderTotal();
    return Math.round(ht * (parseFloat(formData.vat_rate) / 100) * 100) / 100;
  };

  const calculateTtc = () => {
    const ht = calculateOrderTotal();
    return Math.round((ht + calculateVatAmount()) * 100) / 100;
  };

  const calculateTotalRemises = () => {
    // Calculer le total des remises des produits existants + nouveaux produits
    const existingItemsRemises = existingItems.reduce((total, item) => {
      const itemTotal = item.quantity * item.unit_price;
      let remiseAmount = 0;
      
      if (item.remise_type === 'pourcentage') {
        remiseAmount = itemTotal * (item.remise_value / 100);
      } else {
        remiseAmount = item.remise_value;
      }
      
      return total + remiseAmount;
    }, 0);
    
    const newItemsRemises = formData.items.reduce((total, item) => {
      const itemTotal = item.quantity * item.unit_price;
      let remiseAmount = 0;
      
      if (item.remise_type === 'pourcentage') {
        remiseAmount = itemTotal * (item.remise_value / 100);
      } else {
        remiseAmount = item.remise_value;
      }
      
      return total + remiseAmount;
    }, 0);
    
    return existingItemsRemises + newItemsRemises;
  };

  // `envoyer` distingue les DEUX boutons. Le formulaire ne sait pas envoyer : il
  // le DIT au parent, qui enchaîne la création puis l'envoi — et qui peut donc
  // signaler un échec d'envoi sur une commande bel et bien créée.
  const handleSubmit = (envoyer = false) => {
    // Combiner les produits existants modifiés avec les nouveaux produits
    const allItems = [
      ...existingItems,
      ...formData.items
    ];
    
    if (allItems.length === 0) {
      showSnackbar(t('purchaseOrderForm.addOneProduct'), 'warning');
      return;
    }
    
    const submitData = {
      ...formData,
      // ⚠️ APRÈS L'ÉTALEMENT, sinon il réécrit la valeur : `formData` porte la
      // même clé à `''`, et une chaîne vide n'est pas une absence de date.
      // (L'étalement lui-même est hérité ; contrairement à celui corrigé au
      // lot RU3-c, `formData` est ici un état de FORMULAIRE et non un objet
      // reçu du serveur — il ne peut donc pas renvoyer de champ calculé.)
      expected_delivery_date: formData.expected_delivery_date || null,
      items: allItems,
      vat_enabled: formData.vat_enabled,
      vat_rate: formData.vat_enabled && formData.vat_rate !== '' ? parseFloat(formData.vat_rate) : null
    };
    
    onSubmit(submitData, { envoyer });
  };

  const handleClose = () => {
    setFormData({
      supplier_id: '',
      order_date: jourCivilAujourdhui(),
      comment: '',
      items: [],
      vat_enabled: false,
      vat_rate: ''
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                         gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span">
            {editingOrder ? t('purchaseOrderForm.editTitle') : t('purchaseOrderForm.newTitle')}
          </Typography>
          {numeroAffiche && (
            <>
              <Typography variant="body2" color="text.disabled">·</Typography>
              {/* La chasse fixe passe par le jeton unique du thème : elle était
                  recopiée à la main trois fois avec deux valeurs différentes. */}
              <Typography variant="body2" color="text.secondary"
                          sx={{ fontFamily: PILE_CHASSE_FIXE }}>
                {numeroAffiche}
              </Typography>
            </>
          )}
        </Box>
        {/* 🔴 DEUX BOUTONS, ET LA SÉPARATION EXISTE DÉJÀ CÔTÉ SERVEUR — mesuré :
            `create_purchase_order` ne pose que `en_attente_envoi`, et seuls
            `POST /send-email` et `POST /mark-sent` écrivent `envoye`. C'était le
            FRONT qui enchaînait les deux gestes en un seul clic, et un échec
            d'envoi laissait alors une commande dans un état qu'on n'avait pas
            choisi. « Enregistrer » en contour, « Envoyer » en plein : un seul
            geste est mis en avant, et c'est celui qui part chez le fournisseur. */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => handleSubmit(false)} variant="outlined" size="small">
            {t('purchaseOrderForm.save')}
          </Button>
          <Button onClick={() => handleSubmit(true)} variant="contained" size="small"
                  startIcon={<SendIcon />}>
            {t('orders.sendEmail')}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Informations de base */}
          {/* 🔴 FOURNISSEUR · DATE · LIVRAISON PRÉVUE SUR UNE LIGNE. Les deux
              premiers occupaient chacun une demi-largeur, et la livraison prévue
              n'était pas demandée du tout — alors que le serveur la sert et que
              c'est la SEULE date que le détail peut montrer, faute de date de
              réception (décision 12). */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('purchaseOrderForm.supplierLabel')}</InputLabel>
              <Select
                value={formData.supplier_id}
                onChange={(e) => setFormData(prev => ({ ...prev, supplier_id: e.target.value }))}
                label={t('purchaseOrderForm.supplierLabel')}
              >
                {suppliers.map(supplier => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              margin="normal"
              type="date"
              label={t('purchaseOrderForm.orderDate')}
              value={formData.order_date}
              onChange={(e) => setFormData(prev => ({ ...prev, order_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              margin="normal"
              type="date"
              label={t('orders.expectedOn')}
              value={formData.expected_delivery_date}
              onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          

          {/* ═══ AJOUTER UN PRODUIT — UN SEUL CHAMP, PLEINE LARGEUR ═══════════
              🔴 SIX CHAMPS SONT DEVENUS UN. Le bloc demandait produit, quantité,
              prix, type de remise, valeur de remise et un commentaire AVANT de
              pouvoir ajouter une ligne — six décisions pour un geste qu'on répète.
              Le produit suffit : la quantité et le prix prennent une valeur de
              départ et se corrigent DANS le tableau, sous les yeux, à côté du
              total qu'ils font bouger. */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 320px' }} disabled={!formData.supplier_id}>
                <InputLabel>{t('purchaseOrderForm.addProductPlaceholder')}</InputLabel>
                <Select
                  value={selectedProduct}
                  label={t('purchaseOrderForm.addProductPlaceholder')}
                  onChange={(e) => { setSelectedProduct(e.target.value); ajouterProduit(e.target.value); }}
                  endAdornment={(
                    <InputAdornment position="end" sx={{ mr: 2 }}>
                      <IconButton size="small" disabled={!formData.supplier_id}
                                  onClick={() => setScanOuvert(true)}
                                  title={t('purchaseOrderForm.scanBarcode')}>
                        <ScanIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )}
                >
                  {filteredProducts.map((produit) => (
                    <MenuItem key={produit.id} value={produit.id}>
                      {produit.name}{produit.barcode ? ` — ${produit.barcode}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 🔴 « PRODUITS EN ALERTE · N » — ET LA QUANTITÉ COMBLE LE SEUIL.
                  `quantiteACombler` est la MÊME fonction que le dialogue
                  d'ajustement et le sélecteur de réappro : `seuil − stock + 1`,
                  le `+1` parce que l'alerte du serveur est INCLUSIVE. Commander
                  jusqu'au seuil exact laisserait le produit en alerte après
                  réception — un bouton qui ne fait pas ce que son nom annonce.
                  ⚠️ Le bouton est ABSENT quand il n'y a rien à combler, pas grisé :
                  un bouton grisé fait chercher pourquoi. */}
              {produitsEnAlerte.length > 0 && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<AlerteIcon />}
                  onClick={ajouterProduitsEnAlerte}
                  sx={{ flex: '0 0 auto', height: 56 }}
                >
                  {t('purchaseOrderForm.lowStockProducts')} · {produitsEnAlerte.length}
                </Button>
              )}
            </Box>
          </Grid>

            {(formData.items.length > 0 || existingItems.length > 0) && (
             <Grid item xs={12}>
               <Typography variant="h6" gutterBottom>
                 {t('purchaseOrderForm.orderProducts')}
               </Typography>
               <TableContainer component={Paper}>
                 <Table>
                   <TableHead>
                     <TableRow>
                       <TableCell>{t('purchaseOrderForm.colProduct')}</TableCell>
                       <TableCell>{t('purchaseOrderForm.colQuantity')}</TableCell>
                       <TableCell>{t('purchaseOrderForm.colUnitPrice')}</TableCell>
                       <TableCell>{t('purchaseOrderForm.colTotalHt')}</TableCell>
                       <TableCell>{t('purchaseOrderForm.colDiscountType')}</TableCell>
                       <TableCell>{t('purchaseOrderForm.colDiscountValue')}</TableCell>
                       <TableCell>{t('purchaseOrderForm.colTotalWithDiscount')}</TableCell>
                       <TableCell>{t('purchaseOrderForm.colActions')}</TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                                           {/* Produits existants */}
                      {existingItems.map((item, index) => {
                       // 🔴 UNE LIGNE DONT UNE SEULE UNITÉ EST ARRIVÉE EST VERROUILLÉE.
                       // On ne renégocie pas le prix de ce qu'on a déjà rentré en
                       // stock : changer la remise APRÈS la réception fausse la
                       // comptabilité de la commande alors que la marchandise, elle,
                       // a bougé — et l'écran ne montre pas cette conséquence. Même
                       // famille que l'ajustement de stock silencieusement annulé.
                       // ⚠️ LE SEUIL EST « UNE UNITÉ », PAS « TOUT REÇU ». Sur 12
                       // reçus de 30, la ligne est verrouillée : les 12 sont en stock,
                       // et une remise s'applique à la ligne ENTIÈRE, donc aussi à eux.
                       // ⚠️ ET C'EST DE LA DÉFENSE EN PROFONDEUR, DIT OUVERTEMENT :
                       // aujourd'hui le serveur refuse déjà toute modification hors
                       // `en_attente_envoi` (400), état où aucune ligne n'est reçue.
                       // La vérité de l'invariant reposait donc sur une CHAÎNE de
                       // trois faits dans trois fichiers ; la porter sur la ligne
                       // elle-même la rend LOCALE — vraie sans dépendre du reste.
                       const ligneVerrouillee = (item.received_quantity || 0) > 0;
                       return (
                       <TableRow key={`existing-${index}`}>
                         <TableCell>{item.product_name}</TableCell>
                         <TableCell>{item.quantity}</TableCell>
                          <TableCell>{formatCurrency(item.unit_price, currency)}</TableCell>
                          <TableCell>{formatCurrency(item.quantity * item.unit_price, currency)}</TableCell>
                         <TableCell>
                           {ligneVerrouillee ? (
                             <Typography variant="body2" color="text.secondary">
                               {item.remise_type === 'pourcentage'
                                 ? '%' : currencySymbol}
                             </Typography>
                           ) : (
                           <FormControl size="small" fullWidth>
                             <Select
                               value={item.remise_type || 'montant'}
                               onChange={(e) => handleUpdateExistingItem(index, 'remise_type', e.target.value)}
                             >
                              <MenuItem value="montant">{t('purchaseOrderForm.discountTypeAmount')} ({currencySymbol})</MenuItem>
                              <MenuItem value="pourcentage">{t('purchaseOrderForm.discountTypePercent')}</MenuItem>
                             </Select>
                           </FormControl>
                           )}
                         </TableCell>
                         <TableCell>
                           {ligneVerrouillee ? (
                             <Typography variant="body2" color="text.secondary">
                               {item.remise_value || 0}
                             </Typography>
                           ) : (
                           <TextField
                             size="small"
                             type="number"
                             value={item.remise_value || 0}
                             onChange={(e) => handleUpdateExistingItem(index, 'remise_value', parseFloat(e.target.value) || 0)}
                             inputProps={{ min: 0, step: 0.01 }}
                             sx={{ width: 80 }}
                             InputProps={{
                               startAdornment: (
                                 <InputAdornment position="start">
                                   {item.remise_type === 'montant' ? currencySymbol : '%'}
                                 </InputAdornment>
                               ),
                             }}
                           />
                           )}
                         </TableCell>
                          <TableCell>{formatCurrency(calculateItemTotal(item), currency)}</TableCell>
                         <TableCell>
                           {/* Le RETRAIT d'une ligne reçue est pire encore que le
                               changement de sa remise : il efface la trace de ce qui
                               est entré en stock. Même verrou, même raison. */}
                           {!ligneVerrouillee && (
                             <IconButton
                               color="error"
                               onClick={() => handleRemoveExistingItem(index)}
                             >
                               <DeleteIcon />
                             </IconButton>
                           )}
                         </TableCell>
                       </TableRow>
                       );
                     })}
                     
                     {/* Nouveaux produits */}
                     {formData.items.map((item, index) => (
                       <TableRow key={`new-${index}`}>
                         <TableCell>
                           <Typography variant="body2">{item.product_name}</Typography>
                           {/* 🔴 « STOCK 1 / 3 » SOUS LE NOM, EN AMBRE — et seulement
                               si le produit est SOUS LE SEUIL. C'est ce qui dit
                               POURQUOI cette ligne est là quand elle vient du bouton
                               d'alerte, et ce qui permet de juger la quantité sans
                               quitter le formulaire. Affiché sur tous les produits,
                               il ne dirait plus rien : *une information qui ne varie
                               pas cesse d'être une information.* */}
                           {estEnAlerte(item) && (
                             <Typography variant="caption" color="warning.main" display="block">
                               {t('common.currentStock')} {item.current_stock} / {item.min_stock}
                             </Typography>
                           )}
                         </TableCell>
                         {/* Quantité et prix se corrigent ICI, à côté du total
                             qu'ils font bouger — ils étaient en lecture seule, et
                             se fixaient dans un bloc situé au-dessus du tableau. */}
                         <TableCell>
                           <TextField
                             size="small" type="number"
                             value={item.quantity}
                             onChange={(e) => handleUpdateNewItem(index, 'quantity',
                               Math.max(1, parseInt(e.target.value, 10) || 1))}
                             inputProps={{ min: 1 }}
                             sx={{ width: 80 }}
                           />
                         </TableCell>
                         <TableCell>
                           <TextField
                             size="small" type="number"
                             value={item.unit_price}
                             onChange={(e) => handleUpdateNewItem(index, 'unit_price',
                               parseFloat(e.target.value) || 0)}
                             inputProps={{ min: 0, step: 0.01 }}
                             sx={{ width: 100 }}
                             InputProps={{ startAdornment: (
                               <InputAdornment position="start">{currencySymbol}</InputAdornment>) }}
                           />
                         </TableCell>
                          <TableCell>{formatCurrency(item.quantity * item.unit_price, currency)}</TableCell>
                         <TableCell>
                           <FormControl size="small" fullWidth>
                             <Select
                               value={item.remise_type || 'montant'}
                               onChange={(e) => handleUpdateNewItem(index, 'remise_type', e.target.value)}
                             >
                              <MenuItem value="montant">{t('purchaseOrderForm.discountTypeAmount')} ({currencySymbol})</MenuItem>
                              <MenuItem value="pourcentage">{t('purchaseOrderForm.discountTypePercent')}</MenuItem>
                             </Select>
                           </FormControl>
                         </TableCell>
                         <TableCell>
                           <TextField
                             size="small"
                             type="number"
                             value={item.remise_value || 0}
                             onChange={(e) => handleUpdateNewItem(index, 'remise_value', parseFloat(e.target.value) || 0)}
                             inputProps={{ min: 0, step: 0.01 }}
                             sx={{ width: 80 }}
                             InputProps={{
                               startAdornment: (
                                 <InputAdornment position="start">
                                   {item.remise_type === 'montant' ? currencySymbol : '%'}
                                 </InputAdornment>
                               ),
                             }}
                           />
                         </TableCell>
                          <TableCell>{formatCurrency(calculateItemTotal(item), currency)}</TableCell>
                         <TableCell>
                           <IconButton
                             color="error"
                             onClick={() => handleRemoveItem(index)}
                           >
                             <DeleteIcon />
                           </IconButton>
                         </TableCell>
                       </TableRow>
                     ))}
                   </TableBody>
                 </Table>
               </TableContainer>
             </Grid>
           )}

          {/* ═══ EN BAS : LE COMMENTAIRE À GAUCHE, LE RÉCAPITULATIF À DROITE ═══
              Le commentaire vivait EN HAUT, avant même d'avoir choisi un produit —
              on commente une commande qu'on a finie, pas une qu'on commence. Et
              les totaux occupaient une carte pleine largeur : ils se lisent en
              colonne, à droite, où l'œil finit sa course. */}
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('purchaseOrderForm.comment')}
              value={formData.comment}
              onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('orders.totalHT')}
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(calculateOrderTotal(), currency)}
                </Typography>
              </Box>
              {/* La remise en vert : c'est ce qu'on ne paie pas. Absente quand elle
                  vaut zéro — une remise à 0,00 fait chercher où elle est passée. */}
              {calculateTotalRemises() > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('orders.totalDiscounts')}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    −{formatCurrency(calculateTotalRemises(), currency)}
                  </Typography>
                </Box>
              )}
              {/* 🔴 LE CONTRÔLE QUI ACTIVE LA TVA — À NE JAMAIS CONFONDRE AVEC LA
                  LIGNE QUI L'AFFICHE. Une refonte du récapitulatif l'a emporté avec
                  le bloc qu'elle remplaçait : la ligne de TVA restait correctement
                  conditionnée, et il devenait IMPOSSIBLE de créer une commande avec
                  TVA — `vat_enabled` n'avait plus aucun écrivain. *Masquer un
                  CHIFFRE quand il ne s'applique pas, ce n'est pas retirer le moyen
                  de le rendre applicable.*
                  ⚠️ LA CASE, ELLE, EST TOUJOURS RENDUE : c'est le seul chemin vers
                  la TVA, donc elle ne peut pas dépendre de la TVA. */}
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={formData.vat_enabled}
                    onChange={(e) => setFormData(prev => ({
                      ...prev, vat_enabled: e.target.checked }))}
                  />
                )}
                label={(
                  <Typography variant="body2">{t('purchaseOrderForm.applyVat')}</Typography>
                )}
              />
              {formData.vat_enabled && (
                <TextField
                  type="number"
                  size="small"
                  label={t('purchaseOrderForm.vatRate')}
                  value={formData.vat_rate}
                  onChange={(e) => setFormData(prev => ({ ...prev, vat_rate: e.target.value }))}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={{ width: 160, alignSelf: 'flex-end' }}
                />
              )}
              {/* La LIGNE de TVA ne s'affiche que si la case est cochée ET qu'un
                  taux est saisi : « TVA () » sur un taux vide serait pire qu'une
                  ligne absente. */}
              {formData.vat_enabled && formData.vat_rate !== '' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('purchaseOrderForm.vatAmount')}{formData.vat_rate ? ` (${formData.vat_rate}%)` : ''}
                  </Typography>
                  <Typography variant="body2">
                    {formatCurrency(calculateVatAmount(), currency)}
                  </Typography>
                </Box>
              )}
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="body2" color="text.secondary">
                  {formData.vat_enabled ? t('orders.totalTTC') : t('orders.finalTotal')}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {formatCurrency(formData.vat_enabled ? calculateTtc() : calculateOrderTotal(), currency)}
                </Typography>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </DialogContent>
      <DialogActions>
        {/* ⚠️ LES DEUX BOUTONS D'ACTION SONT EN HAUT, avec le numéro — ils y sont
            visibles sans dérouler un formulaire qui peut être long. Ne reste ici
            que la sortie, qui n'écrit rien. Un troisième bouton d'enregistrement
            en bas ferait DEUX chemins pour le même geste, donc deux occasions de
            diverger le jour où l'un des deux changera. */}
        <Button onClick={handleClose}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
      <ZXingBarcodeScanner
        open={scanOuvert}
        onClose={() => setScanOuvert(false)}
        onScan={surCodeScanne}
      />
    </Dialog>
  );
};

export default PurchaseOrderFormWithRemises;
